// Composer Studio — COMPOSED 제목 규칙(titleTemplate)의 토큰 어휘.
//
// 서버 `resolveComposedTitle`(apps/server/src/reports/utils/composed-title.util.ts)의 치환 어휘와
// 짝이다(2026-08-17 결정). 한쪽에만 토큰이 생기면 저작자는 쓸 수 있는 토큰을 모르거나(어휘 누락),
// 없는 토큰을 넣게 된다 — 그래서 이 목록은 그 파일을 정본으로 따라간다.
//
// EditorPage에서 분리한 이유: 어휘 대조는 통렌더 없이 검사할 수 있어야 한다(titleTemplateTokens.test.ts).

import { getSplitAxisProvider } from './splitAxisPolicy';
import type { TemplatePolicy } from '@safetyhub/shared';

/** 제목 규칙 토큰 하나 — 버튼 라벨과 툴팁 힌트. */
export interface TitleTemplateToken {
  /** 삽입되는 리터럴(중괄호 포함). */
  token: string;
  /** 버튼에 보이는 이름. */
  label: string;
  /** 값이 없을 때의 거동 등 툴팁 보충 설명(없으면 삽입 안내만). */
  hint?: string;
}

/**
 * 서버가 치환하는 **전체** 토큰 어휘 — 이 목록이 서버와의 짝을 지키는 정본이다.
 *
 * 버튼에 무엇을 노출할지는 `titleTemplateTokensFor(policy)`가 정한다(전부 늘 보이지는 않는다).
 * 어휘 대조 검사는 노출 목록이 아니라 **이 목록**을 서버 원문과 맞춘다 — 조건부로 감춘 토큰이
 * 어휘에서 빠진 것으로 읽히면 검사가 드리프트를 못 잡는다.
 *
 * `{seq}`·`{group}`은 값이 없으면 **토큰이 붙은 단어째** 제거된다(`{seq}차` → 잔재 없음).
 * 미해소 토큰이 그대로 남지 않으므로 저작은 잔재를 걱정하지 않아도 된다.
 */
export const TITLE_TEMPLATE_TOKENS: readonly TitleTemplateToken[] = [
  { token: '{site}', label: '현장명' },
  { token: '{seq}', label: '차수', hint: '점검이 연결되지 않아 차수를 못 구하면 붙은 단어째 사라집니다.' },
  { token: '{date}', label: '날짜' },
  { token: '{templateName}', label: '템플릿명' },
  { token: '{company}', label: '회사명' },
  {
    token: '{group}',
    label: '그룹명',
    // 서버 resolveComposedTitle: groupLabel이 null(=이번 생성이 분할이 아님)이면 {seq}와 같은 규칙으로
    // 단어째 제거, 토큰을 안 쓴 제목 규칙이라도 분할이면 꼬리에 " - 그룹명"이 자동으로 붙는다.
    // 분할 축이 있는 템플릿도 **작성자가 진입 시 통합을 고를 수 있어**(SplitMode는 정책 축이 아니다)
    // 이 안내는 버튼이 보이는 상황에서도 유효하다.
    hint: '분할 생성한 보고서의 그룹 표시명입니다. 통합으로 생성하면 붙은 단어째 사라지고, 토큰을 넣지 않아도 분할이면 제목 끝에 자동으로 붙습니다.',
  },
];

/**
 * 이 템플릿의 제목 규칙 칸에 **버튼으로 노출할** 토큰 목록.
 *
 * `{group}`은 분할 축(`policy.split.provider`)이 선언된 템플릿에서만 보인다 — 축이 없으면 그
 * 템플릿은 영원히 통합 생성이라 토큰이 늘 사라지고, 버튼은 "쓸 수 있다"는 잘못된 신호가 된다.
 *
 * 감추는 것은 버튼뿐이고 **입력 검증은 하지 않는다.** 손으로 친 `{group}`은 축이 없어도 서버가
 * 안전하게 단어째 지우므로(잔재 없음), 막을 이유가 없다.
 */
export function titleTemplateTokensFor(
  policy: TemplatePolicy | undefined,
): readonly TitleTemplateToken[] {
  if (getSplitAxisProvider(policy) !== '') return TITLE_TEMPLATE_TOKENS;
  return TITLE_TEMPLATE_TOKENS.filter((t) => t.token !== '{group}');
}
